import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { TabPanel, TabView } from 'primereact/tabview';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { exportToExcelHtml, exportMultipleSheetsToExcelHtml } from '../../utils/exportHelper';

// Actions
import { getInterviewTechnology } from '../../redux/actions/hr';

// APIs
import {
  filteredHrListApi,
  getHRStatsApi,
  getAllHRCallLogsReportApi,
  getAllHRInterviewScheduleReportApi,
  getAllHRInterviewRoundReportApi
} from '../../redux/apis/hr';

import { ROUTES, DEBOUNCE_TIMEOUT } from '../../constants/common';
import { renderHrAvatar, isDarkMode } from '../../utils/common';
import { debounce } from '../../utils/debounce';

const DATE_PILLS = ['Today', 'This Week', 'This Month', 'Custom Range'];

const getFilterParams = (activePill, customRange, techFilter) => {
  const durationMap = {
    Today: 'today',
    'This Week': 'week',
    'This Month': 'month',
    'Custom Range': 'custom'
  };
  const duration = durationMap[activePill];

  let startDate = null;
  let endDate = null;
  if (duration === 'custom') {
    const range = customRange && customRange[0] ? customRange : [new Date(), new Date()];
    startDate = dayjs(range[0]).format('YYYY-MM-DD');
    endDate = range[1] ? dayjs(range[1]).format('YYYY-MM-DD') : startDate;
  }

  const technology = techFilter?.length > 0 ? techFilter.join(',') : undefined;

  return { duration, startDate, endDate, technology };
};

const renderActiveStatus = (isActive) => {
  return (
    <div
      className={`rpt-glow-dot ${isActive ? 'rpt-glow-dot--active' : 'rpt-glow-dot--inactive'}`}
      title={isActive ? 'Active Recruiter' : 'Inactive Recruiter'}
    />
  );
};

// ── Rounds Bar Chart Component ────────────────────────────────────────────────
const RoundsBarChart = ({ roundWise, isLight = false }) => {
  const roundKeys = [
    {
      key: 'technical1',
      label: 'Tech 1',
      fullName: 'Technical Interview 1',
      color: isLight ? '#A59DF6' : '#7367F0'
    },
    {
      key: 'practical',
      label: 'Prac',
      fullName: 'Practical Round',
      color: isLight ? '#6BE19E' : '#28C76F'
    },
    {
      key: 'technical2',
      label: 'Tech 2',
      fullName: 'Technical Interview 2',
      color: isLight ? '#5CE7F5' : '#00CFE8'
    },
    {
      key: 'management',
      label: 'Mgmt',
      fullName: 'Management Round',
      color: isLight ? '#FFC183' : '#FF9F43'
    },
    {
      key: 'hr',
      label: 'HR',
      fullName: 'HR Round',
      color: isLight ? '#e27b9c' : '#c72757ff'
    }
  ];

  const total = roundKeys.reduce((acc, item) => acc + (roundWise?.[item.key]?.total || 0), 0);

  if (total === 0) {
    return (
      <div className="rpt-rounds-progress-wrapper">
        <div className="rpt-rounds-progress-bar rpt-rounds-progress-bar--empty">
          <div className="rpt-rounds-progress-empty-msg">No rounds processed</div>
        </div>
      </div>
    );
  }

  // Filter out rounds with no data so we can calculate position
  const activeSegments = roundKeys
    .map((item) => {
      const data = roundWise?.[item.key] || {
        total: 0,
        selected: 0,
        rejected: 0,
        hold: 0,
        pending: 0
      };
      return { ...item, data };
    })
    .filter((item) => item.data.total > 0);

  return (
    <div className="rpt-rounds-progress-wrapper">
      <div className="rpt-rounds-progress-bar">
        {activeSegments.map((item, idx) => {
          const pct = (item.data.total / total) * 100;
          const isFirstSegment = idx === 0;
          const isLastSegment = idx === activeSegments.length - 1;

          let alignmentClass = '';
          if (isFirstSegment) {
            alignmentClass = 'rpt-bar-card--align-left';
          } else if (isLastSegment) {
            alignmentClass = 'rpt-bar-card--align-right';
          }

          return (
            <div
              key={item.key}
              className="rpt-rounds-progress-bar__seg"
              style={{ flex: pct, backgroundColor: item.color }}>
              {/* Tooltip Hover Card */}
              <div className={`rpt-bar-card ${alignmentClass}`}>
                <div className="rpt-bar-card__back-header">{item.fullName}</div>
                <div className="rpt-bar-card__status-list">
                  <div className="rpt-bar-card__status-item">
                    <span className="dot dot--total"></span>
                    <span className="rpt-bar-card__status-name">Total:</span>
                    <span className="rpt-bar-card__status-count">{item.data.total}</span>
                  </div>
                  <div className="rpt-bar-card__status-item">
                    <span className="dot dot--accepted"></span>
                    <span className="rpt-bar-card__status-name">Selected:</span>
                    <span className="rpt-bar-card__status-count">{item.data.selected}</span>
                  </div>
                  <div className="rpt-bar-card__status-item">
                    <span className="dot dot--rejected"></span>
                    <span className="rpt-bar-card__status-name">Rejected:</span>
                    <span className="rpt-bar-card__status-count">{item.data.rejected}</span>
                  </div>
                  <div className="rpt-bar-card__status-item">
                    <span className="dot dot--onhold"></span>
                    <span className="rpt-bar-card__status-name">On Hold:</span>
                    <span className="rpt-bar-card__status-count">{item.data.hold}</span>
                  </div>
                  <div className="rpt-bar-card__status-item">
                    <span className="dot dot--pending"></span>
                    <span className="rpt-bar-card__status-name">Pending:</span>
                    <span className="rpt-bar-card__status-count">{item.data.pending}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="rpt-rounds-progress-legend">
        {activeSegments.map((item) => (
          <span key={item.key} className="rpt-rounds-progress-legend__item">
            <span className="dot" style={{ backgroundColor: item.color }} />
            {item.label}: <strong>{item.data.total}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Call Logs Table ───────────────────────────────────────────────────────────
const CallLogsTable = ({ data, onNavigateToHr }) => {
  const [expanded, setExpanded] = useState({});
  const totalCallsCount = data.reduce((acc, hr) => acc + (hr.totalCalls || 0), 0);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  if (data.length === 0) {
    return <div className="rpt-no-data-msg">No records found</div>;
  }

  return (
    <div className="rpt-admin-table">
      {/* Team Total Row */}
      <div className="rpt-admin-table__total-row">
        <span className="rpt-admin-table__total-label">
          <i className="pi pi-chart-line" /> Team Total Calls
        </span>
        <span className="rpt-admin-table__total-value">{totalCallsCount}</span>
      </div>

      {/* Header Row */}
      <div className="rpt-admin-table__header">
        <span>HR Recruiter</span>
        <span>Total Calls</span>
        <span>Technology Share</span>
      </div>

      {/* HR Rows */}
      {data.map((hr) => {
        const nameParts = hr.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const hasTechData = Object.keys(hr.technologyWiseCalls || {}).length > 0;

        return (
          <div key={hr.id} className="rpt-admin-table__group">
            <div
              className={`rpt-admin-table__hr-row rpt-admin-table__hr-row--clickable ${
                expanded[hr.id] ? 'rpt-admin-table__hr-row--expanded' : ''
              }`}
              onClick={() => toggle(hr.id)}>
              <span className="rpt-admin-table__hr-name-col">
                <button
                  className="rpt-recruiter-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigateToHr) onNavigateToHr(hr.id);
                  }}
                  title={`View ${hr.name}'s full report`}>
                  {renderHrAvatar(hr.id, hr.imagePath, firstName, lastName, false)}
                  <span className="rpt-recruiter-name">{hr.name}</span>
                </button>
                {renderActiveStatus(hr.isActive)}
              </span>

              <span className="rpt-admin-table__hr-count">{hr.totalCalls}</span>

              <span className="rpt-admin-table__hr-bar-col">
                <div className="rpt-mini-bar">
                  <div
                    className="rpt-mini-bar__fill"
                    style={{
                      width: `${totalCallsCount > 0 ? (hr.totalCalls / totalCallsCount) * 100 : 0}%`
                    }}
                  />
                </div>
              </span>
            </div>

            {/* Tech chips breakdown */}
            {expanded[hr.id] && (
              <div className="rpt-admin-table__tech-list-expanded">
                {hasTechData ? (
                  <div className="rpt-tech-chips-container">
                    {Object.entries(hr.technologyWiseCalls).map(([tech, t]) => {
                      const isDark = isDarkMode();
                      const textColor = isDark ? t.colorCode : t.darkModeColorCode;
                      const bgColor = isDark ? `${t.darkModeColorCode}50` : `${t.colorCode}20`;
                      const borderColor = isDark ? `${t.colorCode}30` : `${t.darkModeColorCode}30`;
                      const countBg = isDark ? `${t.colorCode}25` : `${t.darkModeColorCode}20`;
                      return (
                        <div
                          key={tech}
                          className="rpt-tech-chip-custom"
                          style={{
                            backgroundColor: bgColor,
                            color: textColor,
                            border: `1px solid ${borderColor}`
                          }}>
                          <span className="rpt-tech-chip-custom__name">{tech}</span>
                          <span
                            className="rpt-tech-chip-custom__count"
                            style={{ backgroundColor: countBg, color: textColor }}>
                            {t.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rpt-tech-empty">No calls recorded for any technology</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Schedules Table ───────────────────────────────────────────────────────────
const SchedulesTable = ({ data, onNavigateToHr }) => {
  const [expanded, setExpanded] = useState({});
  const totalSchedulesCount = data.reduce((acc, hr) => acc + (hr.totalSchedules || 0), 0);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  if (data.length === 0) {
    return <div className="rpt-no-data-msg">No records found</div>;
  }

  return (
    <div className="rpt-admin-table">
      {/* Team Total Row */}
      <div className="rpt-admin-table__total-row">
        <span className="rpt-admin-table__total-label">
          <i className="pi pi-chart-line" /> Team Total Schedules
        </span>
        <span className="rpt-admin-table__total-value">{totalSchedulesCount}</span>
      </div>

      {/* Header Row */}
      <div className="rpt-admin-table__header">
        <span>HR Recruiter</span>
        <span>Total Schedules</span>
        <span>Technology Share</span>
      </div>

      {/* HR Rows */}
      {data.map((hr) => {
        const nameParts = hr.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const hasTechData = Object.keys(hr.technologyWiseSchedules || {}).length > 0;

        return (
          <div key={hr.id} className="rpt-admin-table__group">
            <div
              className={`rpt-admin-table__hr-row rpt-admin-table__hr-row--clickable ${
                expanded[hr.id] ? 'rpt-admin-table__hr-row--expanded' : ''
              }`}
              onClick={() => toggle(hr.id)}>
              <span className="rpt-admin-table__hr-name-col">
                <button
                  className="rpt-recruiter-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigateToHr) onNavigateToHr(hr.id);
                  }}
                  title={`View ${hr.name}'s full report`}>
                  {renderHrAvatar(hr.id, hr.imagePath, firstName, lastName, false)}
                  <span className="rpt-recruiter-name">{hr.name}</span>
                </button>
                {renderActiveStatus(hr.isActive)}
              </span>

              <span className="rpt-admin-table__hr-count">{hr.totalSchedules}</span>

              <span className="rpt-admin-table__hr-bar-col">
                <div className="rpt-mini-bar">
                  <div
                    className="rpt-mini-bar__fill"
                    style={{
                      width: `${
                        totalSchedulesCount > 0
                          ? (hr.totalSchedules / totalSchedulesCount) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
              </span>
            </div>

            {/* Tech chips breakdown */}
            {expanded[hr.id] && (
              <div className="rpt-admin-table__tech-list-expanded">
                {hasTechData ? (
                  <div className="rpt-tech-chips-container">
                    {Object.entries(hr.technologyWiseSchedules).map(([tech, t]) => {
                      const isDark = isDarkMode();
                      const textColor = isDark ? t.colorCode : t.darkModeColorCode;
                      const bgColor = isDark ? `${t.darkModeColorCode}50` : `${t.colorCode}20`;
                      const borderColor = isDark ? `${t.colorCode}30` : `${t.darkModeColorCode}30`;
                      const countBg = isDark ? `${t.colorCode}25` : `${t.darkModeColorCode}20`;
                      return (
                        <div
                          key={tech}
                          className="rpt-tech-chip-custom"
                          style={{
                            backgroundColor: bgColor,
                            color: textColor,
                            border: `1px solid ${borderColor}`
                          }}>
                          <span className="rpt-tech-chip-custom__name">{tech}</span>
                          <span
                            className="rpt-tech-chip-custom__count"
                            style={{ backgroundColor: countBg, color: textColor }}>
                            {t.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rpt-tech-empty">No interviews scheduled for any technology</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Interview Rounds Table ────────────────────────────────────────────────────
const InterviewRoundsTable = ({ data, onNavigateToHr }) => {
  const [expanded, setExpanded] = useState({});
  const totalRoundsCount = data.reduce((acc, hr) => acc + (hr.totalRounds || 0), 0);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  if (data.length === 0) {
    return <div className="rpt-no-data-msg">No records found</div>;
  }

  return (
    <div className="rpt-admin-table rpt-admin-table--rounds">
      {/* Team Total Row */}
      <div className="rpt-admin-table__total-row">
        <span className="rpt-admin-table__total-label">
          <i className="pi pi-chart-line" /> Team Total Rounds
        </span>
        <span className="rpt-admin-table__total-value">{totalRoundsCount}</span>
      </div>

      {/* Header Row */}
      <div className="rpt-admin-table__header rpt-admin-table__header--rounds">
        <span>HR Recruiter</span>
        <span>Total Rounds</span>
        <span>Overall Rounds Breakdown</span>
      </div>

      {/* HR Rows */}
      {data.map((hr) => {
        const nameParts = hr.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const hasTechData = Object.keys(hr.technologyWiseRounds || {}).length > 0;

        return (
          <div key={hr.id} className="rpt-admin-table__group">
            <div
              className={`rpt-admin-table__hr-row rpt-admin-table__hr-row--clickable rpt-admin-table__hr-row--rounds ${
                expanded[hr.id] ? 'rpt-admin-table__hr-row--expanded' : ''
              }`}
              onClick={() => toggle(hr.id)}>
              <span className="rpt-admin-table__hr-name-col">
                <button
                  className="rpt-recruiter-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigateToHr) onNavigateToHr(hr.id);
                  }}
                  title={`View ${hr.name}'s full report`}>
                  {renderHrAvatar(hr.id, hr.imagePath, firstName, lastName, false)}
                  <span className="rpt-recruiter-name">{hr.name}</span>
                </button>
                {renderActiveStatus(hr.isActive)}
              </span>

              <span className="rpt-admin-table__hr-count">{hr.totalRounds}</span>

              <span className="rpt-admin-table__hr-chart-col">
                <RoundsBarChart roundWise={hr.roundWise} isDark={isDarkMode()} />
              </span>
            </div>

            {/* Accordion rounds chart breakdown per technology */}
            {expanded[hr.id] && (
              <div className="rpt-admin-table__tech-list-expanded rpt-admin-table__tech-list-expanded--rounds">
                {hasTechData ? (
                  <div className="rpt-tech-rounds-container">
                    {Object.entries(hr.technologyWiseRounds).map(([tech, t]) => {
                      const color = isDarkMode() ? t.darkModeColorCode : t.colorCode;
                      return (
                        <div key={tech} className="rpt-tech-round-item-custom">
                          <div className="rpt-tech-round-item-custom__header">
                            <div className="rpt-tech-round-item-custom__name-wrapper">
                              <span
                                className="rpt-tech-round-item-custom__dot"
                                style={{ backgroundColor: color }}
                              />
                              <span className="rpt-tech-round-item-custom__name">{tech}</span>
                            </div>
                            <span className="rpt-tech-round-item-custom__total">
                              Total: <strong>{t.total}</strong>
                            </span>
                          </div>
                          <div className="rpt-tech-round-item-custom__chart">
                            <RoundsBarChart roundWise={t.rounds} isLight={true} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rpt-tech-empty">No rounds recorded for any technology</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const RecruitmentReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inlineCalendarRef = useRef(null);
  const stickyCalendarRef = useRef(null);
  const inlineMultiSelectRef = useRef(null);
  const stickyMultiSelectRef = useRef(null);
  const inlineHrMultiSelectRef = useRef(null);
  const stickyHrMultiSelectRef = useRef(null);
  const dispatch = useDispatch();

  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);

  const [activePill, setActivePill] = useState(() => location.state?.activePill || 'This Month');
  const [techFilter, setTechFilter] = useState(() => location.state?.techFilter || []);
  const [techFilterPending, setTechFilterPending] = useState(
    () => location.state?.techFilter || []
  );
  const [hrFilter, setHrFilter] = useState([]);
  const [hrFilterPending, setHrFilterPending] = useState([]);
  const [customRange, setCustomRange] = useState(() => {
    if (location.state?.customRange?.length) {
      return location.state.customRange.map((d) => new Date(d));
    }
    return [new Date(), new Date()];
  });
  const [activeTab, setActiveTab] = useState(0);
  const [showStickyFilter, setShowStickyFilter] = useState(false);

  const handleNavigateToHr = (hrId) => {
    navigate(ROUTES.ADMIN.HR_REPORT.replace(':ID', hrId), {
      state: { activePill, customRange, techFilter }
    });
  };

  // Debounced setters — update API-trigger states after user stops selecting
  const debouncedSetTechFilter = useMemo(
    () => debounce((value) => setTechFilter(value), DEBOUNCE_TIMEOUT),
    []
  );
  const debouncedSetHrFilter = useMemo(
    () => debounce((value) => setHrFilter(value), DEBOUNCE_TIMEOUT),
    []
  );

  const handleTechFilterChange = (e) => {
    setTechFilterPending(e.value);
    debouncedSetTechFilter(e.value);
  };
  const handleHrFilterChange = (e) => {
    setHrFilterPending(e.value);
    debouncedSetHrFilter(e.value);
  };

  // Stats data
  const [statsData, setStatsData] = useState({
    offers: {
      total: 0,
      outcome: { selected: 0, notSelected: 0, onHold: 0, pending: 0 },
      ratings: {
        offered: 0,
        accepted: 0,
        rejected: 0,
        inDiscussion: 0,
        furtherReview: 0,
        selected: 0
      }
    },
    interviewRounds: {
      total: 0,
      selected: 0,
      rejected: 0,
      onHold: 0,
      pending: 0
    },
    calls: { total: 0, breakdown: {}, previousPeriodTotal: 0, percentageChange: 0 },
    interviews: {
      total: 0,
      selected: 0,
      rejected: 0,
      onHold: 0,
      pending: 0,
      previousPeriodTotal: 0,
      percentageChange: 0
    },
    candidates: { total: 0, breakdown: {} }
  });

  // HR recruiters options
  const [hrOptions, setHrOptions] = useState([]);

  // Lazy-loaded report table data
  const [loadingTab, setLoadingTab] = useState(false);
  const [callsData, setCallsData] = useState([]);
  const [schedulesData, setSchedulesData] = useState([]);
  const [roundsData, setRoundsData] = useState([]);

  // Fetch technologies and recruiters list on mount
  useEffect(() => {
    dispatch(getInterviewTechnology());
    const fetchHRs = async () => {
      try {
        const response = await filteredHrListApi();
        if (response?.status === 200) {
          const sortedHrs = [...(response.data.data || [])].sort((a, b) => {
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            return 0;
          });
          const list = sortedHrs.map((hr) => ({
            label: `${hr.FirstName} ${hr.LastName}${hr.isActive ? '' : ' (Inactive)'}`,
            value: hr.ID,
            isActive: hr.isActive
          }));
          setHrOptions(list);
        }
      } catch (err) {
        console.error('Error fetching HR recruiters:', err);
        toast.error('Error fetching HR recruiters');
      }
    };
    fetchHRs();
  }, [dispatch]);

  // Fetch dashboard widget statistics
  useEffect(() => {
    const fetchWidgetStats = async () => {
      const params = getFilterParams(activePill, customRange, techFilter);
      if (!params) return;
      const { duration, startDate, endDate, technology } = params;

      try {
        let mainStats;
        if (hrFilter.length > 0) {
          const promises = hrFilter.map((hrId) =>
            getHRStatsApi({
              hrId,
              duration,
              startDate,
              endDate,
              technology
            })
          );
          const responses = await Promise.all(promises);

          const aggregated = {
            offers: {
              total: 0,
              outcome: { selected: 0, notSelected: 0, onHold: 0, pending: 0 },
              ratings: {
                offered: 0,
                accepted: 0,
                rejected: 0,
                inDiscussion: 0,
                furtherReview: 0,
                selected: 0
              }
            },
            interviewRounds: {
              total: 0,
              selected: 0,
              rejected: 0,
              onHold: 0,
              pending: 0
            },
            calls: { total: 0, breakdown: {}, previousPeriodTotal: 0, percentageChange: 0 },
            interviews: {
              total: 0,
              selected: 0,
              rejected: 0,
              onHold: 0,
              pending: 0,
              previousPeriodTotal: 0,
              percentageChange: 0
            },
            candidates: { total: 0, breakdown: {} }
          };

          responses.forEach((res) => {
            if (res?.status === 200) {
              const data = res.data.data;
              aggregated.offers.total += data.offers?.total || 0;
              aggregated.offers.outcome.selected +=
                data.offers?.outcome?.selected ?? (data.offers?.selected || 0);
              aggregated.offers.outcome.notSelected +=
                data.offers?.outcome?.notSelected ?? (data.offers?.rejected || 0);
              aggregated.offers.outcome.onHold +=
                data.offers?.outcome?.onHold ?? (data.offers?.onHold || 0);
              aggregated.offers.outcome.pending += data.offers?.outcome?.pending || 0;

              aggregated.offers.ratings.offered += data.offers?.ratings?.offered || 0;
              aggregated.offers.ratings.accepted +=
                data.offers?.ratings?.accepted ?? (data.offers?.accepted || 0);
              aggregated.offers.ratings.rejected +=
                data.offers?.ratings?.rejected ?? (data.offers?.rejected || 0);
              aggregated.offers.ratings.inDiscussion += data.offers?.ratings?.inDiscussion || 0;
              aggregated.offers.ratings.furtherReview += data.offers?.ratings?.furtherReview || 0;
              aggregated.offers.ratings.selected += data.offers?.ratings?.selected || 0;

              const ir = data.interviewRounds || data.interviews || {};
              aggregated.interviewRounds.total += ir.total || 0;
              aggregated.interviewRounds.selected += ir.selected || 0;
              aggregated.interviewRounds.rejected += ir.rejected || 0;
              aggregated.interviewRounds.onHold += ir.onHold || 0;
              aggregated.interviewRounds.pending += ir.pending || 0;

              aggregated.calls.total += data.calls?.total || 0;
              aggregated.calls.previousPeriodTotal += data.calls?.previousPeriodTotal || 0;
              Object.entries(data.calls?.breakdown || {}).forEach(([k, v]) => {
                aggregated.calls.breakdown[k] = (aggregated.calls.breakdown[k] || 0) + v;
              });

              aggregated.interviews.total += data.interviews?.total || 0;
              aggregated.interviews.selected += data.interviews?.selected || 0;
              aggregated.interviews.rejected += data.interviews?.rejected || 0;
              aggregated.interviews.onHold += data.interviews?.onHold || 0;
              aggregated.interviews.pending += data.interviews?.pending || 0;
              aggregated.interviews.previousPeriodTotal +=
                data.interviews?.previousPeriodTotal || 0;

              aggregated.candidates.total += data.candidates?.total || 0;
              Object.entries(data.candidates?.breakdown || {}).forEach(([k, v]) => {
                aggregated.candidates.breakdown[k] = (aggregated.candidates.breakdown[k] || 0) + v;
              });
            }
          });

          const calculatePercentageChange = (curr, prev) => {
            if (prev === 0) return curr > 0 ? 100 : 0;
            return Math.round(((curr - prev) / prev) * 10000) / 100;
          };
          aggregated.calls.percentageChange = calculatePercentageChange(
            aggregated.calls.total,
            aggregated.calls.previousPeriodTotal
          );
          aggregated.interviews.percentageChange = calculatePercentageChange(
            aggregated.interviews.total,
            aggregated.interviews.previousPeriodTotal
          );

          mainStats = aggregated;
        } else {
          const response = await getHRStatsApi({
            hrId: null,
            duration,
            startDate,
            endDate,
            technology
          });
          if (response?.status === 200) {
            mainStats = response.data.data;
          }
        }

        if (mainStats) {
          setStatsData(mainStats);
        }
      } catch (error) {
        console.error('Error fetching admin reports stats:', error);
        toast.error('Error fetching statistics');
      }
    };

    fetchWidgetStats();
  }, [activePill, customRange, techFilter, hrFilter]);

  // Fetch report table data based on active tab
  useEffect(() => {
    const fetchTabReportData = async () => {
      const params = getFilterParams(activePill, customRange, techFilter);
      if (!params) return;
      const { duration, startDate, endDate, technology } = params;

      setLoadingTab(true);
      try {
        const hrParam = hrFilter.length > 0 ? hrFilter.join(',') : undefined;
        if (activeTab === 0) {
          const response = await getAllHRCallLogsReportApi({
            duration,
            startDate,
            endDate,
            technology,
            hr: hrParam
          });
          if (response?.status === 200) {
            const data = response.data.data || [];
            const filtered =
              hrFilter.length > 0 ? data.filter((hr) => hrFilter.includes(hr.id)) : data;
            setCallsData(filtered);
          }
        } else if (activeTab === 1) {
          const response = await getAllHRInterviewScheduleReportApi({
            duration,
            startDate,
            endDate,
            technology,
            hr: hrParam
          });
          if (response?.status === 200) {
            const data = response.data.data || [];
            const filtered =
              hrFilter.length > 0 ? data.filter((hr) => hrFilter.includes(hr.id)) : data;
            setSchedulesData(filtered);
          }
        } else if (activeTab === 2) {
          const response = await getAllHRInterviewRoundReportApi({
            duration,
            startDate,
            endDate,
            technology,
            hr: hrParam
          });
          if (response?.status === 200) {
            const data = response.data.data || [];
            const filtered =
              hrFilter.length > 0 ? data.filter((hr) => hrFilter.includes(hr.id)) : data;
            setRoundsData(filtered);
          }
        }
      } catch (error) {
        console.error(`Error fetching data for tab ${activeTab}:`, error);
        toast.error('Error fetching report details');
      } finally {
        setLoadingTab(false);
      }
    };

    fetchTabReportData();
  }, [activeTab, activePill, customRange, techFilter, hrFilter]);

  // Auto-close overlay lists on page scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyFilter(window.scrollY > 80);
      [inlineCalendarRef, stickyCalendarRef].forEach((ref) => {
        if (ref.current) {
          if (typeof ref.current.hideOverlay === 'function') ref.current.hideOverlay();
          else if (typeof ref.current.hide === 'function') ref.current.hide();
        }
      });
      [
        inlineMultiSelectRef,
        stickyMultiSelectRef,
        inlineHrMultiSelectRef,
        stickyHrMultiSelectRef
      ].forEach((ref) => {
        if (ref.current) {
          if (typeof ref.current.hideOverlay === 'function') ref.current.hideOverlay();
          else if (typeof ref.current.hide === 'function') ref.current.hide();
        }
      });
      if (
        document.activeElement &&
        (document.activeElement.closest('.p-calendar') ||
          document.activeElement.closest('.p-multiselect'))
      ) {
        document.activeElement.blur();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const techOptions =
    technologyListData?.map((tech) => ({
      label: tech.Technology,
      value: tech.ID
    })) || [];

  const renderFilterBar = (isSticky = false) => (
    <div
      className={`rpt-filter-bar ${isSticky ? 'rpt-filter-bar--sticky' : ''} ${
        isSticky && showStickyFilter ? 'rpt-filter-bar--sticky-show' : ''
      }`}>
      <div className="rpt-pill-group">
        {DATE_PILLS.map((pill) => (
          <button
            key={pill}
            className={`rpt-pill ${activePill === pill ? 'rpt-pill--active' : ''}`}
            onClick={() => setActivePill(pill)}>
            {pill}
          </button>
        ))}
      </div>

      {activePill === 'Custom Range' && (
        <Calendar
          ref={isSticky ? stickyCalendarRef : inlineCalendarRef}
          value={customRange}
          onChange={(e) => setCustomRange(e.value || [new Date(), new Date()])}
          onHide={() => {
            if (customRange && customRange[0] && !customRange[1]) {
              setCustomRange([customRange[0], customRange[0]]);
            }
          }}
          selectionMode="range"
          readOnlyInput
          placeholder="Select date range"
          className="rpt-calendar"
          dateFormat="dd/mm/yy"
          showIcon
        />
      )}

      <MultiSelect
        ref={isSticky ? stickyMultiSelectRef : inlineMultiSelectRef}
        value={techFilterPending}
        options={techOptions}
        onChange={handleTechFilterChange}
        placeholder="Filter by Technology"
        display="chip"
        filter
        maxSelectedLabels={2}
        className="rpt-multiselect"
      />

      <MultiSelect
        ref={isSticky ? stickyHrMultiSelectRef : inlineHrMultiSelectRef}
        value={hrFilterPending}
        options={hrOptions}
        onChange={handleHrFilterChange}
        placeholder="Filter by HR Recruiter"
        display="chip"
        filter
        optionLabel="label"
        optionValue="value"
        maxSelectedLabels={2}
        className="rpt-multiselect"
        itemTemplate={(option) => (
          <div
            className={`flex align-items-center ${!option.isActive ? 'inactive-hr-item' : ''}`}
            style={{ position: 'relative', paddingRight: '14px', width: '100%' }}>
            {option.label}
            <span
              className={`hr-status-dot ${option.isActive ? 'active-dot' : 'inactive-dot'}`}
              style={{ position: 'absolute', right: 0, top: '5px' }}></span>
          </div>
        )}
      />
      <div style={{ marginLeft: 'auto' }}>
        <Button
          label="Export All"
          icon="pi pi-file-excel"
          className="p-button-success p-button-sm rpt-export-btn"
          onClick={handleExportAll}
        />
      </div>
    </div>
  );

  const getPeriodLabel = () => {
    if (activePill === 'Custom Range') {
      if (customRange && customRange[0]) {
        const start = customRange[0].toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short'
        });
        const end = customRange[1]
          ? customRange[1].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
          : '';
        return end ? `Team total · ${start} - ${end}` : `Team total · ${start}`;
      }
      return 'Team total · Custom Range';
    }
    return `Team total · ${activePill}`;
  };

  const getTrendSubtext = () => {
    if (activePill === 'Today') return 'vs yesterday';
    if (activePill === 'This Week') return 'vs last week';
    if (activePill === 'This Month') return 'vs last month';
    return '';
  };

  const renderOffersBreakdown = () => {
    const offers = statsData.offers || {};
    const ratings = offers.ratings || {
      offered: 0,
      accepted: offers.accepted || 0,
      rejected: offers.rejected || 0,
      inDiscussion: 0,
      furtherReview: 0,
      selected: 0
    };

    const selected = ratings.selected || 0;
    const offered = ratings.offered || 0;
    const accepted = ratings.accepted || 0;
    const rejected = ratings.rejected || 0;
    const inDiscussion = (ratings.inDiscussion || 0) + (ratings.furtherReview || 0);

    const totalRatings = selected + offered + accepted + rejected + inDiscussion;

    if (totalRatings === 0 && (offers.total || 0) === 0) {
      return <div className="rpt-offers-legend">No offers processed in this period</div>;
    }

    const denom = totalRatings > 0 ? totalRatings : 1;
    const selectedPct = (selected / denom) * 100;
    const offeredPct = (offered / denom) * 100;
    const acceptedPct = (accepted / denom) * 100;
    const rejectedPct = (rejected / denom) * 100;
    const inDiscussionPct = (inDiscussion / denom) * 100;

    return (
      <div className="rpt-offers-wrapper">
        <div className="rpt-offers-bar">
          {selectedPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--selected"
              style={{ flex: selectedPct }}
              title={`Selected: ${selected} (${Math.round(selectedPct)}%)`}
            />
          )}
          {offeredPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--offered"
              style={{ flex: offeredPct }}
              title={`Offered: ${offered} (${Math.round(offeredPct)}%)`}
            />
          )}
          {acceptedPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--accepted"
              style={{ flex: acceptedPct }}
              title={`Accepted: ${accepted} (${Math.round(acceptedPct)}%)`}
            />
          )}
          {rejectedPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--rejected"
              style={{ flex: rejectedPct }}
              title={`Rejected: ${rejected} (${Math.round(rejectedPct)}%)`}
            />
          )}
          {inDiscussionPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--discussion"
              style={{ flex: inDiscussionPct }}
              title={`In Discussion: ${inDiscussion} (${Math.round(inDiscussionPct)}%)`}
            />
          )}
        </div>
        <div className="rpt-offers-legend">
          <span>
            <span className="dot dot--selected" /> Selected: <strong>{selected}</strong>
          </span>
          <span>
            <span className="dot dot--offered" /> Offered: <strong>{offered}</strong>
          </span>
          <span>
            <span className="dot dot--accepted" /> Accepted: <strong>{accepted}</strong>
          </span>
          <span>
            <span className="dot dot--rejected" /> Rejected: <strong>{rejected}</strong>
          </span>
          <span>
            <span className="dot dot--discussion" /> In Discussion: <strong>{inDiscussion}</strong>
          </span>
        </div>
      </div>
    );
  };

  const renderInterviewRoundsBreakdown = () => {
    const rounds = statsData.interviewRounds || statsData.interviews || {};
    const total = rounds.total || 0;
    const selected = rounds.selected || 0;
    const rejected = rounds.rejected || 0;
    const onHold = rounds.onHold || 0;
    const pending = rounds.pending || 0;

    const sum = selected + rejected + onHold + pending;
    if (sum === 0 && total === 0) {
      return <div className="rpt-offers-legend">No interview rounds in this period</div>;
    }

    const denom = sum > 0 ? sum : total || 1;
    const selectedPct = (selected / denom) * 100;
    const rejectedPct = (rejected / denom) * 100;
    const onHoldPct = (onHold / denom) * 100;
    const pendingPct = (pending / denom) * 100;

    return (
      <div className="rpt-offers-wrapper">
        <div className="rpt-offers-bar">
          {selectedPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--accepted"
              style={{ flex: selectedPct }}
              title={`Selected: ${selected} (${Math.round(selectedPct)}%)`}
            />
          )}
          {rejectedPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--rejected"
              style={{ flex: rejectedPct }}
              title={`Rejected: ${rejected} (${Math.round(rejectedPct)}%)`}
            />
          )}
          {onHoldPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--onhold"
              style={{ flex: onHoldPct }}
              title={`On Hold: ${onHold} (${Math.round(onHoldPct)}%)`}
            />
          )}
          {pendingPct > 0 && (
            <div
              className="rpt-offers-bar__seg rpt-offers-bar__seg--pending"
              style={{ flex: pendingPct }}
              title={`Pending: ${pending} (${Math.round(pendingPct)}%)`}
            />
          )}
        </div>
        <div className="rpt-offers-legend">
          <span>
            <span className="dot dot--accepted" /> Selected: <strong>{selected}</strong>
          </span>
          <span>
            <span className="dot dot--rejected" /> Rejected: <strong>{rejected}</strong>
          </span>
          <span>
            <span className="dot dot--onhold" /> On Hold: <strong>{onHold}</strong>
          </span>
          <span>
            <span className="dot dot--pending" /> Pending: <strong>{pending}</strong>
          </span>
        </div>
      </div>
    );
  };

  const renderCandidateSourceBreakdown = () => {
    const breakdown = statsData.candidates.breakdown || {};
    const total = statsData.candidates.total || 0;

    const colors = {
      Manual: '#22B4BE',
      LinkedIn: '#0A66C2',
      Naukri: '#FF5A00',
      Reference: '#6C63FF',
      Career: '#F59E0B'
    };

    const keyMap = {
      MANUAL: 'Manual',
      LinkedIn: 'LinkedIn',
      Naukri: 'Naukri',
      'Employee Reference': 'Reference',
      Career: 'Career'
    };

    const sourcingData = Object.entries(keyMap).map(([apiKey, label]) => {
      const count = breakdown[apiKey] || 0;
      const percent = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        label,
        value: percent,
        color: colors[label] || '#6c757d'
      };
    });

    if (total === 0) {
      return <div className="rpt-sourcing-legend">No candidates added in this period</div>;
    }

    return (
      <div className="rpt-sourcing-wrapper">
        <div className="rpt-sourcing-bar">
          {sourcingData.map((s) => {
            if (s.value === 0) return null;
            return (
              <div
                key={s.label}
                className="rpt-sourcing-bar__seg"
                style={{ flex: s.value, backgroundColor: s.color }}
                title={`${s.label}: ${s.value}%`}
              />
            );
          })}
        </div>
        <div className="rpt-sourcing-legend">
          {sourcingData.map((s) => {
            if (s.value === 0) return null;
            return (
              <span key={s.label}>
                <span className="dot" style={{ backgroundColor: s.color }} />
                {s.label} <strong>{s.value}%</strong>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const handleActiveTabExport = () => {
    if (activeTab === 0) {
      const fileName = 'Calls_Report';
      const rows = callsData.map((hr) => {
        const techs = Object.entries(hr.technologyWiseCalls || {});
        let techStr = 'N/A';
        if (techs.length > 0) {
          techStr = techs.map(([tech, detail]) => `${tech} (${detail.count})`).join(', ');
        }
        return {
          hrName: hr.name,
          email: hr.email,
          status: hr.isActive ? 'Active' : 'Inactive',
          techName: techStr,
          total: hr.totalCalls
        };
      });

      if (rows.length === 0) {
        toast.warning('No data available to export');
        return;
      }

      exportToExcelHtml({
        fileName,
        sheetName: 'All Recruiters Calls Report',
        columns: [
          { header: 'HR Recruiter', key: 'hrName' },
          { header: 'Email', key: 'email' },
          { header: 'Status', key: 'status' },
          { header: 'Technology', key: 'techName' },
          { header: 'HR Total Calls', key: 'total' }
        ],
        data: rows,
        activePill,
        customRange,
        techFilter,
        technologyListData
      });
    } else if (activeTab === 1) {
      const fileName = 'Schedules_Report';
      const rows = schedulesData.map((hr) => {
        const techs = Object.entries(hr.technologyWiseSchedules || {});
        let techStr = 'N/A';
        if (techs.length > 0) {
          techStr = techs.map(([tech, detail]) => `${tech} (${detail.count})`).join(', ');
        }
        return {
          hrName: hr.name,
          email: hr.email,
          status: hr.isActive ? 'Active' : 'Inactive',
          techName: techStr,
          total: hr.totalSchedules
        };
      });

      if (rows.length === 0) {
        toast.warning('No data available to export');
        return;
      }

      exportToExcelHtml({
        fileName,
        sheetName: 'All Recruiters Interview Schedules Report',
        columns: [
          { header: 'HR Recruiter', key: 'hrName' },
          { header: 'Email', key: 'email' },
          { header: 'Status', key: 'status' },
          { header: 'Technology', key: 'techName' },
          { header: 'HR Total Schedules', key: 'total' }
        ],
        data: rows,
        activePill,
        customRange,
        techFilter,
        technologyListData
      });
    } else if (activeTab === 2) {
      const fileName = 'Rounds_Report';
      const rows = roundsData.map((hr) => {
        const techs = Object.entries(hr.technologyWiseRounds || {});
        let techStr = 'N/A';
        if (techs.length > 0) {
          techStr = techs.map(([tech, detail]) => `${tech} (${detail.total})`).join(', ');
        }
        return {
          hrName: hr.name,
          email: hr.email,
          status: hr.isActive ? 'Active' : 'Inactive',
          techName: techStr,
          total: hr.totalRounds
        };
      });

      if (rows.length === 0) {
        toast.warning('No data available to export');
        return;
      }

      exportToExcelHtml({
        fileName,
        sheetName: 'All Recruiters Interview Rounds Report',
        columns: [
          { header: 'HR Recruiter', key: 'hrName' },
          { header: 'Email', key: 'email' },
          { header: 'Status', key: 'status' },
          { header: 'Technology', key: 'techName' },
          { header: 'HR Total Rounds', key: 'total' }
        ],
        data: rows,
        activePill,
        customRange,
        techFilter,
        technologyListData
      });
    }
  };

  const handleExportAll = async () => {
    const params = getFilterParams(activePill, customRange, techFilter);
    if (!params) return;
    const { duration, startDate, endDate, technology } = params;

    toast.info('Preparing all reports export...');

    try {
      const hrParam = hrFilter.length > 0 ? hrFilter.join(',') : undefined;
      const [callsRes, schedulesRes, roundsRes] = await Promise.all([
        getAllHRCallLogsReportApi({ duration, startDate, endDate, technology, hr: hrParam }),
        getAllHRInterviewScheduleReportApi({
          duration,
          startDate,
          endDate,
          technology,
          hr: hrParam
        }),
        getAllHRInterviewRoundReportApi({ duration, startDate, endDate, technology, hr: hrParam })
      ]);

      const processData = (res) => {
        if (res?.status === 200) {
          const raw = res.data.data || [];
          return hrFilter.length > 0 ? raw.filter((hr) => hrFilter.includes(hr.id)) : raw;
        }
        return [];
      };

      const callsAllData = processData(callsRes);
      const schedulesAllData = processData(schedulesRes);
      const roundsAllData = processData(roundsRes);

      const formatCallsRows = (data) =>
        data.map((hr) => {
          const techs = Object.entries(hr.technologyWiseCalls || {});
          let techStr = 'N/A';
          if (techs.length > 0) {
            techStr = techs.map(([tech, detail]) => `${tech} (${detail.count})`).join(', ');
          }
          return {
            hrName: hr.name,
            email: hr.email,
            status: hr.isActive ? 'Active' : 'Inactive',
            techName: techStr,
            total: hr.totalCalls
          };
        });

      const formatSchedulesRows = (data) =>
        data.map((hr) => {
          const techs = Object.entries(hr.technologyWiseSchedules || {});
          let techStr = 'N/A';
          if (techs.length > 0) {
            techStr = techs.map(([tech, detail]) => `${tech} (${detail.count})`).join(', ');
          }
          return {
            hrName: hr.name,
            email: hr.email,
            status: hr.isActive ? 'Active' : 'Inactive',
            techName: techStr,
            total: hr.totalSchedules
          };
        });

      const formatRoundsRows = (data) =>
        data.map((hr) => {
          const techs = Object.entries(hr.technologyWiseRounds || {});
          let techStr = 'N/A';
          if (techs.length > 0) {
            techStr = techs.map(([tech, detail]) => `${tech} (${detail.total})`).join(', ');
          }
          return {
            hrName: hr.name,
            email: hr.email,
            status: hr.isActive ? 'Active' : 'Inactive',
            techName: techStr,
            total: hr.totalRounds
          };
        });

      exportMultipleSheetsToExcelHtml({
        fileName: 'All_Recruiters_Reports',
        sheets: [
          {
            sheetName: 'Call Logs',
            columns: [
              { header: 'HR Recruiter', key: 'hrName' },
              { header: 'Email', key: 'email' },
              { header: 'Status', key: 'status' },
              { header: 'Technology', key: 'techName' },
              { header: 'HR Total Calls', key: 'total' }
            ],
            data: formatCallsRows(callsAllData)
          },
          {
            sheetName: 'Interview Schedules',
            columns: [
              { header: 'HR Recruiter', key: 'hrName' },
              { header: 'Email', key: 'email' },
              { header: 'Status', key: 'status' },
              { header: 'Technology', key: 'techName' },
              { header: 'HR Total Schedules', key: 'total' }
            ],
            data: formatSchedulesRows(schedulesAllData)
          },
          {
            sheetName: 'Interview Rounds',
            columns: [
              { header: 'HR Recruiter', key: 'hrName' },
              { header: 'Email', key: 'email' },
              { header: 'Status', key: 'status' },
              { header: 'Technology', key: 'techName' },
              { header: 'HR Total Rounds', key: 'total' }
            ],
            data: formatRoundsRows(roundsAllData)
          }
        ],
        activePill,
        customRange,
        techFilter,
        technologyListData
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to export all reports');
    }
  };

  return (
    <div className="rpt-recruitment-report">
      {/* ── Static Filter Bar at the top ── */}
      {renderFilterBar(false)}

      {/* ── Stat Widgets ── */}
      <div className="rpt-widgets">
        {/* Sourcing Mix Widget */}
        <div className="rpt-widget rpt-widget--sourcing">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon">
              <i className="pi pi-share-alt" />
            </span>
            <span className="rpt-widget__title" title="Sourcing Mix">
              Sourcing Mix
            </span>
          </div>
          {renderCandidateSourceBreakdown()}
        </div>

        {/* Outbound Calls Widget */}
        <div className="rpt-widget rpt-widget--stat rpt-widget--admin-calls">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--calls">
              <i className="pi pi-phone" />
            </span>
            <span className="rpt-widget__title" title="Outbound Calls">
              Outbound Calls
            </span>
          </div>
          <div className="rpt-stat-value">{statsData.calls.total}</div>
          <div className="rpt-stat-sub">{getPeriodLabel()}</div>
          {activePill !== 'Custom Range' && (
            <div
              className={`rpt-stat-trend ${
                statsData.calls.percentageChange >= 0
                  ? 'rpt-stat-trend--up'
                  : 'rpt-stat-trend--down'
              }`}>
              <i
                className={`pi ${
                  statsData.calls.percentageChange >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'
                }`}
              />
              {Math.abs(statsData.calls.percentageChange)}% {getTrendSubtext()}
            </div>
          )}
        </div>

        {/* Scheduled Interviews Widget */}
        <div className="rpt-widget rpt-widget--stat rpt-widget--admin-schedule">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--interviews">
              <i className="pi pi-calendar-plus" />
            </span>
            <span className="rpt-widget__title" title="Scheduled Interviews">
              Scheduled Interviews
            </span>
          </div>
          <div className="rpt-stat-value">{statsData.interviews.total}</div>
          <div className="rpt-stat-sub">{getPeriodLabel()}</div>
          {activePill !== 'Custom Range' && (
            <div
              className={`rpt-stat-trend ${
                statsData.interviews.percentageChange >= 0
                  ? 'rpt-stat-trend--up'
                  : 'rpt-stat-trend--down'
              }`}>
              <i
                className={`pi ${
                  statsData.interviews.percentageChange >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'
                }`}
              />
              {Math.abs(statsData.interviews.percentageChange)}% {getTrendSubtext()}
            </div>
          )}
        </div>

        {/* Interview Rounds Widget */}
        <div className="rpt-widget rpt-widget--stat rpt-widget--admin-rounds">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--rounds">
              <i className="pi pi-check-circle" />
            </span>
            <span className="rpt-widget__title" title="Interview Rounds">
              Interview Rounds
            </span>
          </div>
          <div className="rpt-stat-value">{statsData.interviewRounds?.total || 0}</div>
          <div className="rpt-stat-sub">{getPeriodLabel()}</div>
          {renderInterviewRoundsBreakdown()}
        </div>

        {/* Total Offers Widget */}
        <div className="rpt-widget rpt-widget--stat rpt-widget--admin-offers">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--offers">
              <i className="pi pi-briefcase" />
            </span>
            <span className="rpt-widget__title" title="Offers Pipeline">
              Offers Pipeline
            </span>
          </div>
          <div className="rpt-stat-value">
            {statsData.offers?.outcome?.selected ??
              statsData.offers?.selected ??
              (statsData.offers?.total || 0)}
          </div>
          <div className="rpt-stat-sub">
            {statsData.offers?.total > 0 && `(of ${statsData.offers.total} processed)`}
          </div>
          {renderOffersBreakdown()}
        </div>
      </div>

      {/* ── Sticky Filter Bar ── */}
      {renderFilterBar(true)}

      {/* ── Report Tabs Card ── */}
      <div className="rpt-tabs-card">
        <div className="rpt-tabs-card__export-wrapper">
          <Button
            label="Export Excel"
            icon="pi pi-file-excel"
            className="p-button-success p-button-sm rpt-export-btn"
            onClick={handleActiveTabExport}
          />
        </div>
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel
            header={
              <span>
                <i className="pi pi-phone" /> Calls
              </span>
            }>
            {loadingTab ? (
              <div className="rpt-loading-spinner">
                <i className="pi pi-spin pi-spinner" /> Loading Calls Report...
              </div>
            ) : (
              <CallLogsTable data={callsData} onNavigateToHr={handleNavigateToHr} />
            )}
          </TabPanel>

          <TabPanel
            header={
              <span>
                <i className="pi pi-calendar" /> Schedules
              </span>
            }>
            {loadingTab ? (
              <div className="rpt-loading-spinner">
                <i className="pi pi-spin pi-spinner" /> Loading Schedules Report...
              </div>
            ) : (
              <SchedulesTable data={schedulesData} onNavigateToHr={handleNavigateToHr} />
            )}
          </TabPanel>

          <TabPanel
            header={
              <span>
                <i className="pi pi-sitemap" /> Rounds
              </span>
            }>
            {loadingTab ? (
              <div className="rpt-loading-spinner">
                <i className="pi pi-spin pi-spinner" /> Loading Rounds Report...
              </div>
            ) : (
              <InterviewRoundsTable data={roundsData} onNavigateToHr={handleNavigateToHr} />
            )}
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
};

export default RecruitmentReport;
